function finiteQuotaNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Normalize credit fields from check_quota* responses.
 * available = credits remaining before this action (462 in 462 → 1 → 461 UI).
 */
export function resolveWelcomeQuotaDisplayCredits(result) {
  if (!result || typeof result !== "object") {
    return {
      available: null,
      required: null,
      afterAction: null,
      total: null,
      used: null,
    };
  }

  const total = finiteQuotaNumber(result.ai_credits_total);
  const used = finiteQuotaNumber(result.ai_credits_used);
  let available =
    finiteQuotaNumber(result.ai_credits_remaining) ??
    finiteQuotaNumber(result.current_quota) ??
    finiteQuotaNumber(result.remaining_quota);

  if (available == null && total != null && used != null) {
    available = Math.max(0, total - used);
  }

  const required = finiteQuotaNumber(result.required_amount) ?? 0;
  const afterAction =
    available != null ? Math.max(0, available - required) : null;

  return { available, required, afterAction, total, used };
}

/**
 * Maps quota check API payload to a user-facing message when applicable === false.
 * Prefers stable not_applicable_reason / required_amount over raw English message.
 */
export function resolveQuotaNotApplicableMessage(result, t) {
  if (!result || result.applicable !== false) {
    return "";
  }
  const zeroOps =
    result.not_applicable_reason === "zero_operations" ||
    Number(result.required_amount) === 0;
  if (zeroOps) {
    if (result.quota_type === "essential_ai") {
      return t("welcome.quotaNotApplicableZeroReviewsEssential");
    }
    return t("welcome.quotaNotApplicableZeroReviewsAdvanced");
  }
  if (typeof result.message === "string" && result.message.trim()) {
    return result.message.trim();
  }
  return t("welcome.quotaCheckNotApplicable");
}

/**
 * Explains advanced_ai credit usage from check_quota* response in plain language.
 */
export function formatWelcomeAiCreditBreakdown(result, t, options = {}) {
  if (!result || result.unlimited) {
    return null;
  }
  const credits = Number(result.required_amount);
  if (!Number.isFinite(credits) || credits <= 0) {
    return null;
  }
  const metrics = Number(result.billable_widget_count);

  // Review-based billing (no vendor name selected): 1 credit per N reviews.
  if (result.credit_mode === "review") {
    const reviews =
      Number(result.credit_review_count ?? result.total_review_count) || 0;
    const chunk = Number(result.review_credits_chunk) || 2000;
    const perMetric =
      chunk > 0 ? Math.max(1, Math.ceil(reviews / chunk)) : credits;
    if (Number.isFinite(metrics) && metrics > 1) {
      return t("welcome.quotaCreditsReviewScopeMultiMetric", {
        reviews,
        metrics,
        credits,
        chunk,
        perMetric,
      });
    }
    return t("welcome.quotaCreditsReviewScope", {
      reviews,
      credits,
      chunk,
      perMetric,
    });
  }

  const hotels = Number(result.hotel_count ?? result.credit_months);
  const pivotKey =
    (typeof options.pivotKeyLabel === "string" && options.pivotKeyLabel.trim()) ||
    t("welcome.quotaCreditsPivotFallback");

  if (Number.isFinite(hotels) && hotels > 1) {
    if (Number.isFinite(metrics) && metrics > 1) {
      return t("welcome.quotaCreditsPivotScopeMultiMetric", {
        pivotKey,
        hotels,
        metrics,
      });
    }
    return t("welcome.quotaCreditsPivotScope", { pivotKey, hotels });
  }

  if (Number.isFinite(metrics) && metrics > 1) {
    return t("welcome.quotaCreditsSingleHotelMultiMetric", { metrics });
  }

  return t("welcome.quotaCreditsSingleUse");
}

export function welcomeGenaiReviewsInsufficient(result) {
  return Boolean(result?.welcome_genai_insufficient_reviews);
}

/** True only when remaining AI credits are less than required for this action. */
export function welcomeAiCreditsInsufficient(result) {
  if (!result || result.error === true || result.applicable === false) {
    return false;
  }
  if (result.unlimited) {
    return false;
  }
  const { available, required } = resolveWelcomeQuotaDisplayCredits(result);
  if (available != null) {
    return available < (required ?? 0);
  }
  return (
    result.sufficient === false && !welcomeGenaiReviewsInsufficient(result)
  );
}

/**
 * True when regenerate can run immediately without the credit confirmation modal
 * (KPI-only refresh, failed retry, or quota check reports zero AI credits).
 */
export function welcomeRegenerateCanProceedWithoutQuotaModal(quotaResult) {
  if (!quotaResult || quotaResult.error === true) {
    return false;
  }
  if (quotaResult.welcome_genai_insufficient_reviews) {
    return false;
  }
  if (welcomeAiCreditsInsufficient(quotaResult)) {
    return false;
  }
  const required = Number(quotaResult.required_amount);
  return !Number.isFinite(required) || required <= 0;
}
